import React, { useEffect, useMemo, useState } from 'react';
import { checkPasswordStrength, SPECIAL_CHARS_DISPLAY } from '../utils/passwordPolicy';
import PasswordInput from '../components/PasswordInput';
import InlineOtpField from './InlineOtpField';
import { inp, lbl, btn, btnDisabled } from './PromptStep';

// Purpose-built renderer for registration's OWN ak-stage-prompt (identity:
// username/email/phone/password/password_repeat) — NOT a drop-in
// replacement for the shared PromptStep.jsx (still used unchanged by
// AddEmail.jsx/AddPhone.jsx/Login.jsx for their own, unrelated
// single-field prompts). See the project plan (2026-09-18, "pre-account
// phone/email verification") for why this exists as its own component:
// phone/email verification now has to happen INLINE, on this same page,
// before the person can submit at all — a page-refresh-safe replacement
// for the old post-submission bescom-phone-pending/bescom-email-pending
// gate, which only ever worked by riding TOTP's mandatory interactive
// pause and provided no real protection once TOTP became optional.
//
// Username availability is checked live (debounced) against the new
// GET /auth/register/check-username — a UX optimization only; Authentik's
// own sso-username-unique Expression Policy is still the authoritative
// check at actual submission (handles the rare race of someone else
// claiming it in between).

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';

const EMAIL_RE = /^[^\s@]+@([^\s@]+)$/;
const ALLOWED_EMAIL_DOMAINS = [
  'bescom.co.in',
  'gmail.com',
  'yahoo.com',
  'outlook.com',
  'hotmail.com',
  'rediffmail.com',
];

const USERNAME_CHECK_DEBOUNCE_MS = 500;

export default function RegistrationIdentityStep({ fields, values, setValues, loading, onSubmit, dark }) {
  const labelStyle = dark ? { ...lbl, color: 'rgba(255, 255, 255, 0.85)' } : lbl;
  const errorColor = dark ? '#FF8A80' : '#B3261E';
  const okColor = dark ? '#7EE0A8' : '#1E7A3D';
  const mutedColor = dark ? 'rgba(255,255,255,0.6)' : '#8a8a86';

  const handleChange = (key, val) => setValues((prev) => ({ ...prev, [key]: val }));
  const minOrder = Math.min(...fields.map((f) => f.order));
  const [touched, setTouched] = useState({});
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  // 'idle' | 'checking' | 'available' | 'taken' | 'invalid'
  const [usernameStatus, setUsernameStatus] = useState('idle');
  const [phoneVerifiedValue, setPhoneVerifiedValue] = useState(null);
  const [emailVerifiedValue, setEmailVerifiedValue] = useState(null);

  const username = (values.username || '').trim();

  useEffect(() => {
    if (!username || /[\s:]/.test(username)) {
      setUsernameStatus('idle');
      return;
    }
    setUsernameStatus('checking');
    const handle = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/register/check-username?username=${encodeURIComponent(username)}`, {
          credentials: 'include',
        });
        const data = await res.json();
        setUsernameStatus(data.available ? 'available' : (data.reason === 'invalid' ? 'invalid' : 'taken'));
      } catch (e) {
        // Best-effort UX check only — Authentik's own policy is still the
        // real gate at submission, so a network hiccup here shouldn't
        // block anything permanently.
        setUsernameStatus('idle');
      }
    }, USERNAME_CHECK_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [username]);

  const phoneValue = (values.phone || '').trim();
  const emailValue = (values['attributes.pending_email'] || '').trim().toLowerCase();
  // Verification is recorded server-side PER USERNAME (see /step's
  // enforcement), so a locally-verified flag is only valid for the username
  // it was verified under — changing the username afterward invalidates it.
  const phoneKey = `${phoneValue}|${username}`;
  const emailKey = `${emailValue}|${username}`;
  const phoneVerifiedHere = !!phoneValue && phoneKey === phoneVerifiedValue;
  const emailVerifiedHere = !!emailValue && emailKey === emailVerifiedValue;

  // Live "already registered?" feedback for phone/email, same idea as the
  // username check above. Only fires once the value is complete/plausible
  // (the backend scans all users, so not worth running per keystroke).
  // { checked: <the value this result is for>, ok, error } — comparing
  // `checked` to the current value keeps a stale, late response from
  // labelling a value the person has since edited.
  const [phoneCheck, setPhoneCheck] = useState(null);
  const [emailCheck, setEmailCheck] = useState(null);

  const emailPlausible = (() => {
    const m = emailValue.match(EMAIL_RE);
    return !!m && ALLOWED_EMAIL_DOMAINS.includes(m[1].toLowerCase());
  })();

  useEffect(() => {
    if (!/^\d{10}$/.test(phoneValue)) { setPhoneCheck(null); return; }
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/phone-verify/check?phone=${encodeURIComponent(phoneValue)}&username=${encodeURIComponent(username)}`);
        const data = await res.json();
        if (!cancelled) setPhoneCheck(res.ok ? { checked: phoneKey, ok: !!data.ok, error: data.error, verified: !!data.verified } : { checked: phoneKey, ok: true });
      } catch (e) {
        // Best-effort UX check — /send still enforces it for real, so a
        // failed check must not strand the person without a Send button.
        if (!cancelled) setPhoneCheck({ checked: phoneKey, ok: true });
      }
    }, USERNAME_CHECK_DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [phoneValue, phoneKey, username]);

  useEffect(() => {
    if (!emailPlausible) { setEmailCheck(null); return; }
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/email-verify/check?email=${encodeURIComponent(emailValue)}&username=${encodeURIComponent(username)}`);
        const data = await res.json();
        if (!cancelled) setEmailCheck(res.ok ? { checked: emailKey, ok: !!data.ok, error: data.error, verified: !!data.verified } : { checked: emailKey, ok: true });
      } catch (e) {
        // Best-effort UX check — /send still enforces it for real, so a
        // failed check must not strand the person without a Send button.
        if (!cancelled) setEmailCheck({ checked: emailKey, ok: true });
      }
    }, USERNAME_CHECK_DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [emailValue, emailKey, emailPlausible, username]);

  const phoneChecking = /^\d{10}$/.test(phoneValue) && !(phoneCheck && phoneCheck.checked === phoneKey);
  const emailChecking = emailPlausible && !(emailCheck && emailCheck.checked === emailKey);
  // The live check also reports when THIS username already verified this
  // exact value (closed tab, rejected submit, page refresh) — pick up where
  // the person left off instead of making them redo the code.
  const phoneServerVerified = !!(phoneCheck && phoneCheck.checked === phoneKey && phoneCheck.ok && phoneCheck.verified);
  const emailServerVerified = !!(emailCheck && emailCheck.checked === emailKey && emailCheck.ok && emailCheck.verified);
  const phoneVerified = phoneVerifiedHere || phoneServerVerified;
  const emailVerified = emailVerifiedHere || emailServerVerified;

  const phoneTakenMsg = phoneCheck && phoneCheck.checked === phoneKey && !phoneCheck.ok ? phoneCheck.error : null;
  const emailTakenMsg = emailCheck && emailCheck.checked === emailKey && !emailCheck.ok ? emailCheck.error : null;

  const fieldErrors = useMemo(() => {
    const errors = {};
    for (const field of fields) {
      const val = values[field.field_key] || '';
      if (!val) continue;
      if (field.field_key === 'attributes.pending_email') {
        const match = val.match(EMAIL_RE);
        if (!match) {
          errors[field.field_key] = 'Enter a valid email address (e.g. name@gmail.com).';
        } else if (!ALLOWED_EMAIL_DOMAINS.includes(match[1].toLowerCase())) {
          errors[field.field_key] = `Use an email ending in one of: ${ALLOWED_EMAIL_DOMAINS.join(', ')}.`;
        }
      }
      if (field.field_key === 'username') {
        if (/[\s:]/.test(val)) {
          errors[field.field_key] = 'Username cannot contain spaces or colons.';
        } else if (usernameStatus === 'taken') {
          errors[field.field_key] = 'That username is already taken.';
        }
      }
      if (field.field_key === 'password') {
        const strengthError = checkPasswordStrength(val, { username, phone: phoneValue });
        if (strengthError) errors[field.field_key] = strengthError;
      }
      if (field.field_key === 'password_repeat' && values.password && val !== values.password) {
        errors[field.field_key] = 'Passwords do not match.';
      }
    }
    return errors;
  }, [fields, values, usernameStatus, username, phoneValue]);

  const hasFieldErrors = Object.keys(fieldErrors).length > 0;
  const phoneFieldHasValue = fields.some((f) => f.field_key === 'phone') && /^\d{10}$/.test(phoneValue);
  const emailFieldHasValue = fields.some((f) => f.field_key === 'attributes.pending_email') && EMAIL_RE.test(emailValue);
  // Email is MANDATORY (2026-10-31) — must be present and verified,
  // regardless of phone. Phone stays fully optional: if given, it must be
  // verified; if left blank, that's fine on its own. Mirrors the
  // authoritative check in registration.js's own /step route — this is
  // just the UX-convenience copy of it (see that file's own comment).
  const contactChannelsSatisfied =
    emailFieldHasValue && emailVerified &&
    (!phoneFieldHasValue || phoneVerified);
  const usernameReady = !!username && !/[\s:]/.test(username) && usernameStatus === 'available';
  const canSubmit = usernameReady && contactChannelsSatisfied && !hasFieldErrors
    && !!values.password && !!values.password_repeat;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setAttemptedSubmit(true);
        if (!canSubmit) return;
        onSubmit();
      }}
    >
      {fields.map((field) => {
        const takenUsername = field.field_key === 'username' && usernameStatus === 'taken';
        const showError = (touched[field.field_key] || attemptedSubmit || takenUsername) && fieldErrors[field.field_key];
        const isPhone = field.field_key === 'phone';
        const isEmail = field.field_key === 'attributes.pending_email';
        const isUsername = field.field_key === 'username';
        return (
          <div key={field.field_key} style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>{field.label}{(field.required || isEmail) ? ' *' : ''}</label>
            {field.type === 'password' ? (
              <PasswordInput
                style={inp}
                placeholder={field.placeholder || ''}
                required={field.required}
                disabled={!contactChannelsSatisfied}
                value={values[field.field_key] || ''}
                onChange={(e) => handleChange(field.field_key, e.target.value)}
                onBlur={() => setTouched((prev) => ({ ...prev, [field.field_key]: true }))}
                autoFocus={field.order === minOrder}
              />
            ) : (
              <input
                style={inp}
                type={field.type === 'email' ? 'email' : 'text'}
                inputMode={isPhone ? 'numeric' : undefined}
                pattern={isPhone ? '[0-9]{10}' : undefined}
                maxLength={isPhone ? 10 : undefined}
                placeholder={field.placeholder || ''}
                required={isEmail || field.required}
                value={values[field.field_key] || ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  const val = isPhone ? raw.replace(/\D/g, '').slice(0, 10) : raw;
                  handleChange(field.field_key, val);
                }}
                onBlur={() => setTouched((prev) => ({ ...prev, [field.field_key]: true }))}
                autoFocus={field.order === minOrder}
              />
            )}

            {showError ? (
              <p style={{ color: errorColor, fontSize: '14px', margin: '4px 0 0' }}>
                {fieldErrors[field.field_key]}
              </p>
            ) : isUsername && usernameStatus === 'checking' ? (
              <p style={{ color: mutedColor, fontSize: '12px', margin: '4px 0 0' }}>Checking availability…</p>
            ) : isUsername && usernameStatus === 'available' ? (
              <p style={{ color: okColor, fontSize: '12px', margin: '4px 0 0' }}>✓ Available</p>
            ) : field.field_key === 'password' && !contactChannelsSatisfied ? (
              <p style={{ color: mutedColor, fontSize: '12px', margin: '4px 0 0' }}>
                Verify your email above to continue{phoneFieldHasValue && !phoneVerified ? ' (and your phone number, since you entered one)' : ''}.
              </p>
            ) : field.field_key === 'password' && (
              <p style={{ color: mutedColor, fontSize: '12px', margin: '4px 0 0' }}>
                Must be 8+ characters with letters, numbers, and a special character ({SPECIAL_CHARS_DISPLAY})
              </p>
            )}

            {isPhone && phoneChecking && (
              <p style={{ color: mutedColor, fontSize: '12px', margin: '4px 0 0' }}>Checking availability…</p>
            )}
            {isEmail && emailChecking && (
              <p style={{ color: mutedColor, fontSize: '12px', margin: '4px 0 0' }}>Checking availability…</p>
            )}
            {isPhone && phoneTakenMsg && (
              <p style={{ color: errorColor, fontSize: '14px', margin: '4px 0 0' }}>{phoneTakenMsg}</p>
            )}
            {isEmail && emailTakenMsg && (
              <p style={{ color: errorColor, fontSize: '14px', margin: '4px 0 0' }}>{emailTakenMsg}</p>
            )}
            {isPhone && phoneServerVerified && !phoneVerifiedHere && (
              <p style={{ color: okColor, fontSize: '13px', margin: '4px 0 0', fontWeight: '500' }}>✓ Verified</p>
            )}
            {isPhone && phoneFieldHasValue && !usernameReady && !phoneServerVerified && !phoneTakenMsg && (
              <p style={{ color: mutedColor, fontSize: '12px', margin: '4px 0 0' }}>Choose an available username first, then verify this number.</p>
            )}
            {isPhone && usernameReady && phoneFieldHasValue && !fieldErrors[field.field_key] && !phoneTakenMsg && !phoneChecking && !(phoneServerVerified && !phoneVerifiedHere) && (
              <InlineOtpField
                key={username}
                channel="phone"
                value={phoneValue}
                username={username}
                onVerified={() => setPhoneVerifiedValue(phoneKey)}
                dark={dark}
              />
            )}
            {isEmail && emailServerVerified && !emailVerifiedHere && (
              <p style={{ color: okColor, fontSize: '13px', margin: '4px 0 0', fontWeight: '500' }}>✓ Verified</p>
            )}
            {isEmail && emailFieldHasValue && !usernameReady && !emailServerVerified && !emailTakenMsg && (
              <p style={{ color: mutedColor, fontSize: '12px', margin: '4px 0 0' }}>Choose an available username first, then verify this email.</p>
            )}
            {isEmail && usernameReady && emailFieldHasValue && !fieldErrors[field.field_key] && !emailTakenMsg && !emailChecking && !(emailServerVerified && !emailVerifiedHere) && (
              <InlineOtpField
                key={username}
                channel="email"
                value={emailValue}
                username={username}
                onVerified={() => setEmailVerifiedValue(emailKey)}
                dark={dark}
              />
            )}
          </div>
        );
      })}

      {attemptedSubmit && !contactChannelsSatisfied && (
        <p style={{ color: errorColor, fontSize: '13px', margin: '0 0 12px' }}>
          {!emailFieldHasValue || !emailVerified
            ? 'Please verify your email address before continuing.'
            : 'Please verify your phone number before continuing.'}
        </p>
      )}

      <button type="submit" disabled={loading || !canSubmit} style={(loading || !canSubmit) ? btnDisabled : btn}>
        {loading ? 'Creating your account… this can take a few seconds' : 'Continue'}
      </button>
    </form>
  );
}
